import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Plus, Minus, X, Save, Search, Circle, Layers, Trophy, ChevronDown } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { flagFor, specialIconFor, isCocaCola, pageMatchesQuery } from '../lib/flags';
import { useI18n } from '../lib/i18n';

type Sticker = {
  id: string;
  number: number;
  code: string | null;
  rarity: 'common' | 'shiny' | 'legend';
  page_id: string;
  player_name: string | null;
  team: string | null;
  page_number: number;
  page_title: string | null;
  quantity: number;
};

function displayOf(s: { code?: string | null; number: number }): string {
  return s.code ? s.code.replace(/-/g, ' ') : String(s.number);
}

/**
 * Book view + manual entry.
 *
 * Estados visuales (tres colores distintos):
 *  • FALTA      → slot dashed gris (vacío)
 *  • TENGO      → card lime con ✓ y glow lime
 *  • REPETIDA   → card cyan con badge ×N y glow cyan (color distinto al "tengo")
 *
 * Búsqueda: filtra por número, nombre de jugador/etiqueta, equipo y nombre de página.
 *  Ej: "Argentina" → solo página Argentina
 *      "Capitán"   → todos los capitanes de todos los equipos
 *      "12"        → sticker #12 o página 12
 */
export function ManualEntry() {
  const { token } = useAuth();
  const qc = useQueryClient();
  const { t } = useI18n();
  const album = 'mundial-2026';

  const { data: detail } = useQuery({
    queryKey: ['album', album],
    queryFn: () => api.albumDetail(album, token!),
  });
  const { data: col } = useQuery({
    queryKey: ['collection'],
    queryFn: () => api.collection(token!),
  });

  const [pending, setPending] = useState<Map<string, number>>(new Map());
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const stickers: Sticker[] = col?.stickers ?? [];

  // Agrupar TODA la colección por página (igual que MyAlbum)
  const groupedByPage = useMemo(() => {
    if (!detail?.pages) return [];
    const byPage = new Map<number, Sticker[]>();
    for (const s of stickers) {
      if (!byPage.has(s.page_number)) byPage.set(s.page_number, []);
      byPage.get(s.page_number)!.push(s);
    }
    return detail.pages
      .map((p: any) => {
        const items = (byPage.get(p.page_number) ?? []).sort(
          (a, b) => (a as any).position - (b as any).position,
        );
        return {
          pageNumber: p.page_number,
          title: p.title ?? `Página ${p.page_number}`,
          items,
        };
      })
      .sort((a: any, b: any) => a.pageNumber - b.pageNumber);
  }, [stickers, detail]);

  // Filtrar por búsqueda — devuelve país completo + matches por jugador en otros países
  const searching = query.trim().length > 0;
  const q = query.trim().toLowerCase();
  const { countryMatches, playerMatches } = useMemo(() => {
    if (!searching) return { countryMatches: groupedByPage as any[], playerMatches: [] as any[] };
    const cm = groupedByPage.filter((g: any) => pageMatchesQuery(g.title, q));
    const cmIds = new Set(cm.map((g: any) => g.pageNumber));
    const pm = groupedByPage
      .filter((g: any) => !cmIds.has(g.pageNumber))
      .map((g: any) => ({
        ...g,
        items: g.items.filter(
          (s: Sticker) =>
            String(s.number) === q ||
            (s.code?.toLowerCase().includes(q) ?? false) ||
            (s.player_name?.toLowerCase().includes(q) ?? false),
        ),
      }))
      .filter((g: any) => g.items.length > 0);
    return { countryMatches: cm, playerMatches: pm };
  }, [groupedByPage, q, searching]);

  const filteredGroups = useMemo(
    () => [...countryMatches, ...playerMatches],
    [countryMatches, playerMatches],
  );

  // Auto-expand cuando buscás · colapsar al limpiar el search
  useEffect(() => {
    if (searching) {
      setExpanded(new Set(filteredGroups.map((g: any) => g.pageNumber)));
    } else {
      setExpanded(new Set());
    }
  }, [searching, filteredGroups]);

  function toggleExpand(pn: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(pn) ? next.delete(pn) : next.add(pn);
      return next;
    });
  }

  const getQty = (id: string, base: number) =>
    pending.has(id) ? pending.get(id)! : base;
  const setQty = (id: string, q: number) =>
    setPending((m) => new Map(m).set(id, Math.max(0, q)));

  const saveMut = useMutation({
    mutationFn: async () => {
      const updates = [...pending.entries()].map(([stickerId, quantity]) => ({
        stickerId, quantity,
      }));
      return api.bulkUpdate(updates, token!);
    },
    onSuccess: () => {
      confetti({
        particleCount: 100, spread: 65, origin: { y: 0.4 },
        colors: ['#c6ff5e', '#5ee0ff', '#ffd166', '#ff3d6b'],
      });
      setPending(new Map());
      qc.invalidateQueries({ queryKey: ['collection'] });
      qc.invalidateQueries({ queryKey: ['missing'] });
      qc.invalidateQueries({ queryKey: ['duplicates'] });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });

  const pendingCount = pending.size;

  return (
    <div className="space-y-5">
      <header>
        <p className="text-2xs uppercase tracking-widest text-haze">{t('manual.eyebrow')}</p>
        <h1 className="h-display text-3xl">{t('manual.title')}</h1>
        <p className="mt-1 text-sm text-haze">
          {t('manual.tip')}
        </p>
      </header>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-haze" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('common.searchPlaceholder')}
          className="input pl-9"
        />
      </div>

      {/* Resumen rápido */}
      {!query && col && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <StatPill icon={<Check size={14} />} value={col.have} label={t('manual.statHave')} tone="lime" />
          <StatPill icon={<Circle size={12} />} value={col.missing} label={t('manual.statMissing')} tone="flame" />
          <StatPill icon={<Layers size={14} />} value={col.duplicates} label={t('manual.statDupes')} tone="cyan" />
          <StatPill icon={<Trophy size={14} />} value={`${col.completionPct}%`} label={t('manual.statComplete')} tone="gold" />
        </div>
      )}

      {/* Lista desplegable de países */}
      <div className="space-y-2">
        {(() => {
          const renderRow = (g: any) => {
            const have = g.items.filter((i: Sticker) => getQty(i.id, i.quantity) >= 1).length;
            const total = g.items.length;
            const pct = total ? Math.round((have / total) * 100) : 0;
            const isOpen = expanded.has(g.pageNumber);
            const dirtyInGroup = g.items.some((i: Sticker) => pending.has(i.id));
            return (
              <PageRowEditable
                key={g.pageNumber}
                title={g.title}
                flag={flagFor(g.title)}
                have={have}
                total={total}
                pct={pct}
                isOpen={isOpen}
                dirty={dirtyInGroup}
                onToggle={() => toggleExpand(g.pageNumber)}
              >
                {(() => {
                  const items: Sticker[] = g.items;
                  const render = (s: Sticker) => {
                    const qty = getQty(s.id, s.quantity);
                    const dirty = pending.has(s.id);
                    const state: 'missing' | 'have' | 'dup' =
                      qty === 0 ? 'missing' : qty >= 2 ? 'dup' : 'have';
                    return (
                      <StickerTile
                        key={s.id}
                        sticker={s}
                        quantity={qty}
                        state={state}
                        dirty={dirty}
                        onToggle={() => setQty(s.id, qty === 0 ? 1 : 0)}
                        onInc={() => setQty(s.id, qty + 1)}
                        onDec={() => setQty(s.id, Math.max(0, qty - 1))}
                      />
                    );
                  };
                  const legends = items.filter((s) => s.rarity === 'legend');
                  const shinies = items.filter((s) => s.rarity === 'shiny');
                  const players = items.filter((s) => s.rarity === 'common');
                  const isCountryPage =
                    legends.length === 1 && shinies.length === 1 && players.length > 0;
                  if (isCountryPage) {
                    return (
                      <>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <div className="col-span-1">{render(legends[0])}</div>
                          <div className="col-span-2">{render(shinies[0])}</div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                          {players.map(render)}
                        </div>
                      </>
                    );
                  }
                  return (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                      {items.map(render)}
                    </div>
                  );
                })()}
              </PageRowEditable>
            );
          };

          if (!searching) return filteredGroups.map(renderRow);

          return (
            <>
              {countryMatches.length > 0 && (
                <>
                  <h3 className="text-2xs uppercase tracking-widest text-haze font-bold ml-1 mt-1">
                    {t('album.searchCountries')} · {countryMatches.length}
                  </h3>
                  {countryMatches.map(renderRow)}
                </>
              )}
              {playerMatches.length > 0 && (
                <>
                  <h3 className="text-2xs uppercase tracking-widest text-haze font-bold ml-1 mt-3">
                    {t('album.searchPlayers')} · {playerMatches.reduce((s: number, g: any) => s + g.items.length, 0)}
                  </h3>
                  {playerMatches.map(renderRow)}
                </>
              )}
              {countryMatches.length === 0 && playerMatches.length === 0 && (
                <div className="glass rounded-2xl p-8 text-center text-haze text-sm">
                  {t('album.noResults')}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* FOOTER flotante — sólo cuando hay cambios */}
      <AnimatePresence>
        {pendingCount > 0 && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            className="fixed bottom-24 inset-x-4 z-30 mx-auto max-w-md md:max-w-2xl lg:max-w-3xl glass-strong p-3 flex items-center justify-between gap-3"
            style={{ boxShadow: '0 14px 36px -8px rgba(255,61,107,0.55)' }}
          >
            <div>
              <div className="font-semibold text-sm">{pendingCount} {t('common.changes')}</div>
              <div className="text-2xs text-haze">{t('manual.savePending')}</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPending(new Map())}
                className="grid h-10 w-10 place-items-center rounded-full border border-line bg-void/40 text-haze"
                aria-label={t('common.cancel')}
              >
                <X size={16} />
              </button>
              <button
                onClick={() => saveMut.mutate()}
                disabled={saveMut.isPending}
                className="btn-primary px-4 py-2 text-sm"
              >
                <Save size={14} /> {saveMut.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// PageRowEditable — fila desplegable estilo MyAlbum con stickers editables
// ────────────────────────────────────────────────────────────────────────
function PageRowEditable({
  title, flag, have, total, pct, isOpen, dirty, onToggle, children,
}: {
  title: string;
  flag: string | null;
  have: number;
  total: number;
  pct: number;
  isOpen: boolean;
  dirty: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  const isComplete = total > 0 && have === total;
  const isUntouched = have === 0;
  const accent = isComplete ? 'lime' : isUntouched ? 'flame' : 'cyan';
  const accentClasses = {
    lime: {
      border: 'border-lime/40',
      glow: 'shadow-[0_0_24px_-10px_rgba(198,255,94,0.55)]',
      barFill: 'bg-lime',
      text: 'text-lime',
      iconBg: 'bg-lime/15 border-lime/30',
    },
    flame: {
      border: 'border-flame/30',
      glow: '',
      barFill: 'bg-flame/70',
      text: 'text-[#FF6B7A]',
      iconBg: 'bg-flame/10 border-flame/25',
    },
    cyan: {
      border: 'border-line/60',
      glow: 'shadow-[0_0_18px_-12px_rgba(94,224,255,0.4)]',
      barFill: 'bg-gradient-to-r from-lime to-cyan',
      text: 'text-cyan',
      iconBg: 'bg-cyan/10 border-cyan/25',
    },
  }[accent];
  return (
    <div className={`glass-strong rounded-2xl border ${accentClasses.border} ${accentClasses.glow} overflow-hidden ${dirty ? 'ring-2 ring-flame/30' : ''}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition"
      >
        {isCocaCola(title) ? (
          <div className="shrink-0 grid h-12 w-12 rounded-xl place-items-center bg-flame border-2 border-flame font-display font-extrabold text-2xl text-white shadow-glow-flame italic">
            C
          </div>
        ) : (
          <div className={`shrink-0 grid h-12 w-12 rounded-xl place-items-center text-2xl ${accentClasses.iconBg} border`}>
            {flag ?? specialIconFor(title) ?? '✦'}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-display font-extrabold text-base truncate">{title}</h3>
            <span className={`text-2xs font-bold tabular-nums shrink-0 ${accentClasses.text}`}>
              {have}/{total}
              {isComplete && <Check size={11} className="inline ml-1" />}
            </span>
          </div>
          <div className="mt-1.5 h-1 rounded-full bg-white/8 overflow-hidden">
            <motion.div
              initial={false}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className={`h-full ${accentClasses.barFill}`}
            />
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-2xs text-haze tabular-nums">{pct}%</span>
            {dirty && <span className="text-2xs text-flame font-bold">· {t('common.changes')}</span>}
            {isUntouched && !dirty && <span className="text-2xs text-haze italic">· {t('album.untouched')}</span>}
          </div>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="shrink-0 text-haze"
        >
          <ChevronDown size={20} />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-4 pt-1 border-t border-line/40">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// StatPill — chip glass con número grande + label
// ────────────────────────────────────────────────────────────────────────
function StatPill({
  icon, value, label, tone,
}: {
  icon: React.ReactNode;
  value: number | string;
  label: string;
  tone: 'lime' | 'flame' | 'cyan' | 'gold';
}) {
  const t = {
    lime:  { ring: 'border-lime/35  bg-lime/8',   text: 'text-lime',     glow: 'shadow-[0_0_20px_-8px_rgba(198,255,94,0.55)]' },
    flame: { ring: 'border-flame/35 bg-flame/8',  text: 'text-[#FF6B7A]', glow: 'shadow-[0_0_20px_-8px_rgba(230,0,18,0.55)]' },
    cyan:  { ring: 'border-cyan/35  bg-cyan/8',   text: 'text-cyan',     glow: 'shadow-[0_0_20px_-8px_rgba(94,224,255,0.55)]' },
    gold:  { ring: 'border-gold/35  bg-gold/8',   text: 'text-gold',     glow: 'shadow-[0_0_20px_-8px_rgba(255,209,102,0.55)]' },
  }[tone];
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${t.ring} ${t.glow} px-3 py-2.5 flex items-center gap-2.5`}>
      <div className={`shrink-0 grid h-8 w-8 place-items-center rounded-xl bg-ink/40 border border-line/40 ${t.text}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className={`font-display font-extrabold text-xl leading-none tabular-nums ${t.text} truncate`}>
          {value}
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-wider text-haze truncate">
          {label}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// StickerTile — 3 estados visuales distintos
// ────────────────────────────────────────────────────────────────────────
function StickerTile({
  sticker, quantity, state, dirty, onToggle, onInc, onDec,
}: {
  sticker: Sticker;
  quantity: number;
  state: 'missing' | 'have' | 'dup';
  dirty: boolean;
  onToggle: () => void;
  onInc: () => void;
  onDec: () => void;
}) {
  const ringDirty = dirty ? 'ring-2 ring-flame/70' : '';
  const isLegend = sticker.rarity === 'legend';   // escudo
  const isShiny  = sticker.rarity === 'shiny';    // foto del equipo
  const specialLabel = isLegend ? 'ESCUDO' : isShiny ? 'EQUIPO' : null;

  // Falta — back-design Panini, layout estructurado sin overlap
  if (state === 'missing') {
    const accent = isLegend ? 'gold' : isShiny ? 'cyan' : 'haze';
    return (
      <button
        onClick={onToggle}
        className={`group relative ${isShiny ? 'aspect-[3/2]' : 'aspect-[3/4]'} w-full rounded-2xl overflow-hidden grid grid-rows-[auto_1fr_auto] p-2 transition active:scale-[0.97] ${ringDirty}`}
        style={{
          background: isLegend
            ? 'radial-gradient(120% 80% at 50% 0%, rgba(255,209,102,0.08), rgba(255,209,102,0.02) 60%, rgba(10,14,26,0.4))'
            : isShiny
              ? 'radial-gradient(120% 80% at 50% 0%, rgba(94,224,255,0.10), rgba(94,224,255,0.02) 60%, rgba(10,14,26,0.4))'
              : 'radial-gradient(120% 80% at 50% 0%, rgba(255,255,255,0.04), rgba(255,255,255,0.01) 60%, rgba(10,14,26,0.4))',
          border: isLegend
            ? '1.5px dashed rgba(255,209,102,0.45)'
            : isShiny
              ? '1.5px dashed rgba(94,224,255,0.45)'
              : '1.5px dashed rgba(255,255,255,0.16)',
        }}
      >
        {/* watermark 26 FIFA trophy */}
        <svg
          aria-hidden
          viewBox="0 0 100 100"
          className="absolute inset-0 m-auto h-[70%] w-[70%] opacity-[0.05] pointer-events-none"
          fill="currentColor"
        >
          <path d="M30 15h40v6c0 9-4 17-12 21v8h6v6h-6v8h6v6H36v-6h6v-8h-6v-6h6v-8c-8-4-12-12-12-21v-6zm6 6v0c0 6 4 12 10 15v0c6-3 10-9 10-15H36z"/>
        </svg>

        {/* row 1 — icon top-left + add top-right */}
        <div className="relative z-10 flex items-start justify-between">
          {specialLabel ? (
            <span className={`text-[9px] font-bold tracking-[0.18em] ${isLegend ? 'text-gold' : 'text-cyan'}`}>
              {specialLabel}
            </span>
          ) : <span />}
          <span className={`grid h-5 w-5 place-items-center rounded-full bg-void/70 border border-line text-${accent}/70 group-hover:text-${accent} transition`}>
            <Plus size={11} />
          </span>
        </div>

        {/* row 2 — flag + code + name */}
        <div className="relative z-10 flex flex-col items-center justify-center gap-0.5 min-h-0">
          {flagFor(sticker.team) && (
            <span className={`leading-none ${specialLabel ? 'text-2xl' : 'text-lg'} opacity-80`}>
              {flagFor(sticker.team)}
            </span>
          )}
          <span className={`font-mono font-bold leading-tight text-foam/85 ${specialLabel ? 'text-base' : 'text-sm'}`}>
            {displayOf(sticker)}
          </span>
          {sticker.player_name && !specialLabel && (
            <span className="mt-0.5 px-1 text-[10px] text-haze line-clamp-1 text-center">
              {sticker.player_name}
            </span>
          )}
        </div>

        {/* row 3 — FALTA label */}
        <div className="relative z-10 text-center">
          <span className="text-[9px] tracking-[0.22em] text-haze/45 font-medium">FALTA</span>
        </div>
      </button>
    );
  }

  // Tengo — lime
  if (state === 'have') {
    return (
      <div
        className={`relative ${isShiny ? 'aspect-[3/2]' : 'aspect-[3/4]'} rounded-xl overflow-hidden transition ${ringDirty}`}
        style={{
          background: 'linear-gradient(160deg, rgba(198,255,94,0.18) 0%, rgba(198,255,94,0.05) 100%)',
          border: '1px solid rgba(198,255,94,0.45)',
          boxShadow: '0 0 24px -4px rgba(198,255,94,0.4), inset 0 0 0 1px rgba(255,255,255,0.04)',
        }}
      >
        <button onClick={onToggle} className="w-full h-full flex flex-col items-center justify-center pt-2 pb-10">
          {sticker.rarity !== 'common' && (
            <span className={`absolute top-1 left-1 z-10 rounded-md px-1 py-0.5 text-2xs font-bold uppercase tracking-wider ${
              sticker.rarity === 'legend' ? 'bg-gold text-ink' : 'bg-cyan text-ink'
            }`}>
              {sticker.rarity === 'legend' ? '★' : '◆'}
            </span>
          )}
          <Check size={16} className="text-lime mb-1" />
          <span className="font-mono font-bold text-base text-foam leading-tight text-center">{displayOf(sticker)}</span>
          {sticker.player_name && sticker.rarity === 'common' && (
            <span className="mt-0.5 px-1 text-2xs text-foam/90 line-clamp-1 text-center">
              {sticker.player_name}
            </span>
          )}
          {sticker.team && (
            <span className="px-1 text-2xs text-lime/80 line-clamp-1 text-center uppercase tracking-wider">
              {sticker.team}
            </span>
          )}
          <span className="absolute bottom-7 inset-x-0 text-center text-[9px] tracking-[0.22em] text-lime font-bold uppercase">
            La tengo
          </span>
        </button>
        <div className="absolute bottom-0 inset-x-0 flex items-stretch border-t border-lime/20 bg-ink/30">
          <button onClick={onDec} className="flex-1 py-1 text-haze hover:text-flame grid place-items-center">
            <Minus size={12} />
          </button>
          <button onClick={onInc} className="flex-1 py-1 text-lime hover:text-cyan grid place-items-center border-l border-lime/20">
            <Plus size={12} />
          </button>
        </div>
      </div>
    );
  }

  // Repetida — cyan
  return (
    <div
      className={`relative ${isShiny ? 'aspect-[3/2]' : 'aspect-[3/4]'} rounded-xl overflow-hidden transition ${ringDirty}`}
      style={{
        background: 'linear-gradient(160deg, rgba(94,224,255,0.20) 0%, rgba(94,224,255,0.05) 100%)',
        border: '1px solid rgba(94,224,255,0.50)',
        boxShadow: '0 0 24px -4px rgba(94,224,255,0.55), inset 0 0 0 1px rgba(255,255,255,0.04)',
      }}
    >
      <button onClick={onToggle} className="w-full h-full flex flex-col items-center justify-center pt-2 pb-7">
        {sticker.rarity !== 'common' && (
          <span className={`absolute top-1 left-1 z-10 rounded-md px-1 py-0.5 text-2xs font-bold uppercase tracking-wider ${
            sticker.rarity === 'legend' ? 'bg-gold text-ink' : 'bg-cyan text-ink'
          }`}>
            {sticker.rarity === 'legend' ? '★' : '◆'}
          </span>
        )}
        <span className="absolute top-1 right-1 z-10 grid h-7 w-7 place-items-center rounded-full bg-cyan text-ink font-bold text-xs shadow-[0_0_18px_rgba(94,224,255,0.7)]">
          ×{quantity}
        </span>
        <span className="font-mono font-bold text-base text-foam leading-tight text-center">{displayOf(sticker)}</span>
        {sticker.player_name && (
          <span className="mt-0.5 px-1 text-2xs text-foam/90 line-clamp-1 text-center">
            {sticker.player_name}
          </span>
        )}
        {sticker.team && (
          <span className="px-1 text-2xs text-cyan/80 line-clamp-1 text-center uppercase tracking-wider flex items-center justify-center gap-1">
            {flagFor(sticker.team) && <span className="text-sm leading-none">{flagFor(sticker.team)}</span>}
            {sticker.team}
          </span>
        )}
        <span className="absolute bottom-7 left-1 right-1 text-center text-2xs uppercase tracking-widest text-cyan/80 font-semibold">
          repetida
        </span>
      </button>
      <div className="absolute bottom-0 inset-x-0 flex items-stretch border-t border-cyan/20 bg-ink/30">
        <button onClick={onDec} className="flex-1 py-1 text-haze hover:text-flame grid place-items-center">
          <Minus size={12} />
        </button>
        <button onClick={onInc} className="flex-1 py-1 text-cyan grid place-items-center border-l border-cyan/20">
          <Plus size={12} />
        </button>
      </div>
    </div>
  );
}
